import { useQuery } from "@tanstack/react-query";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";

/**
 * Report data comes from the `report_*` RPCs, never from client-side
 * aggregation of listed rows: PostgREST caps a list at 1000 rows, so a summed
 * page would quietly understate a growing workspace's numbers.
 */

export type ReportName =
  | "executive"
  | "sales"
  | "marketing"
  | "tasks"
  | "finance"
  | "geography"
  | "forecast";

export interface ReportFilters {
  from: string;
  to: string;
  ownerId?: number | null;
  pipelineId?: string | null;
}

/** Local YYYY-MM-DD, `toISOString` would shift the day west of UTC. */
export const toDateInput = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;

export type PresetRange =
  | "this_month"
  | "last_30"
  | "this_quarter"
  | "this_year"
  | "custom";

export const presetRange = (
  preset: Exclude<PresetRange, "custom">,
): { from: string; to: string } => {
  const now = new Date();
  const today = toDateInput(now);
  switch (preset) {
    case "this_month":
      return {
        from: toDateInput(new Date(now.getFullYear(), now.getMonth(), 1)),
        to: today,
      };
    case "last_30": {
      const start = new Date(now);
      start.setDate(start.getDate() - 29);
      return { from: toDateInput(start), to: today };
    }
    case "this_quarter": {
      const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
      return {
        from: toDateInput(new Date(now.getFullYear(), quarterStartMonth, 1)),
        to: today,
      };
    }
    case "this_year":
      return {
        from: toDateInput(new Date(now.getFullYear(), 0, 1)),
        to: today,
      };
  }
};

const argsFor = (
  name: ReportName,
  workspaceId: string | null,
  filters: ReportFilters,
): Record<string, unknown> => {
  const base = {
    p_workspace_id: workspaceId,
    p_from: filters.from,
    p_to: filters.to,
  };
  if (name === "sales") {
    return {
      ...base,
      p_owner_id: filters.ownerId ?? null,
      p_pipeline_id: filters.pipelineId ?? null,
    };
  }
  if (name === "tasks") {
    return { ...base, p_owner_id: filters.ownerId ?? null };
  }
  // The forecast is forward-looking from today; the period picker does not
  // apply to it.
  if (name === "forecast") {
    return { p_workspace_id: workspaceId, p_months: 6 };
  }
  return base;
};

export const useReport = <T>(name: ReportName, filters: ReportFilters) => {
  const workspaceId = getActiveWorkspaceId();

  return useQuery<T>({
    queryKey: ["report", name, workspaceId, filters],
    enabled: Boolean(workspaceId),
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc(
        `report_${name}`,
        argsFor(name, workspaceId, filters),
      );
      // A permission refusal is a real answer from the server; surfacing it
      // beats rendering a dashboard full of zeros the user cannot explain.
      if (error) throw new Error(error.message);
      return data as T;
    },
  });
};
